import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "../../lib/siteInfo";
import { LaunchCard, inputClass, inputStyle } from "./ui";

const CHUNK = 2 * 1024 * 1024;

async function uploadInChunks(file, onProgress) {
  const uploadId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let index = 0; index < total; index += 1) {
    const form = new FormData();
    form.append("upload_id", uploadId);
    form.append("index", String(index));
    form.append("total", String(total));
    form.append("chunk", file.slice(index * CHUNK, (index + 1) * CHUNK), "part");
    await api.post("/admin/transfer/upload", form);
    onProgress(Math.round(((index + 1) / total) * 100));
  }
  return uploadId;
}

function PreviewTable({ preview, picked, toggle }) {
  return (
    <div className="mt-4" data-testid="transfer-import-preview">
      <div className="text-xs mb-2" style={{ color: "var(--muted)" }}>
        From <span className="font-mono">{preview.source}</span> ({preview.environment}, v{preview.version}) · exported {formatWhen(preview.created_at)} by {preview.created_by}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="eyebrow text-left border-b" style={{ borderColor: "var(--border)" }}>
          <th className="py-2 pr-2">Import</th><th className="py-2 pr-2">Part</th><th className="py-2 pr-2 text-right">New</th><th className="py-2 text-right">Already here</th></tr></thead>
        <tbody>
          {preview.sections.map((s) => (
            <tr key={s.key} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`transfer-preview-row-${s.key}`}>
              <td className="py-2 pr-2"><input type="checkbox" checked={picked.includes(s.key)} onChange={() => toggle(s.key)} data-testid={`transfer-import-section-${s.key}`} /></td>
              <td className="py-2 pr-2" style={{ color: "var(--ink)" }}>{s.label}</td>
              <td className="py-2 pr-2 text-right font-semibold" data-testid={`transfer-preview-new-${s.key}`}>{s.new.toLocaleString()}</td>
              <td className="py-2 text-right" style={{ color: "var(--muted)" }}>{s.existing.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TransferImport({ environment }) {
  const [file, setFile] = useState(null);
  const [pass, setPass] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploadId, setUploadId] = useState("");
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    setBusy(true); setResult(null);
    try {
      const id = uploadId || await uploadInChunks(file, setProgress);
      setUploadId(id);
      const { data } = await api.post("/admin/transfer/preview", { upload_id: id, passphrase: pass });
      setPreview(data);
      setPicked(data.sections.map((s) => s.key));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!window.confirm("Import the ticked parts? A safety backup is taken first.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/transfer/commit", { upload_id: uploadId, passphrase: pass, sections: picked, update_existing: updateExisting });
      setResult(data); setPreview(null); setUploadId(""); setFile(null);
      toast.success(`Imported: ${data.added} added, ${data.updated} updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = (key) => setPicked(picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key]);

  return (
    <LaunchCard eyebrow={`Into ${environment}`} title="Import from dev-portal" testId="transfer-import-card">
      <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>Upload the export file, check the preview, then import. Existing records are kept unless you choose to update them.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="field-label">Export file (.efx)</label>
          <input type="file" accept=".efx" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] || null); setUploadId(""); setPreview(null); setProgress(0); }} data-testid="transfer-import-file" /></div>
        <div><label className="field-label">Passphrase</label>
          <input type="password" className={inputClass} style={inputStyle} value={pass} onChange={(e) => setPass(e.target.value)} data-testid="transfer-import-passphrase" /></div>
      </div>
      <button type="button" className="btn-ghost mt-4" disabled={!file || pass.length < 12 || busy} onClick={check} data-testid="transfer-import-preview-btn">
        {busy && !preview ? `Checking… ${progress}%` : "Preview import"}
      </button>
      {preview && (
        <>
          <PreviewTable preview={preview} picked={picked} toggle={toggle} />
          <label className="flex items-center gap-2 text-sm mt-3" style={{ color: "var(--ink)" }}>
            <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="transfer-import-update-existing" />
            Also update records that are already here
          </label>
          <button type="button" className="btn-primary mt-4" disabled={!picked.length || busy} onClick={commit} data-testid="transfer-import-commit-btn">{busy ? "Importing…" : "Import ticked parts"}</button>
        </>
      )}
      {result && (
        <div className="mt-4 text-sm border p-3" style={{ borderColor: "var(--border)" }} data-testid="transfer-import-result">
          {result.added.toLocaleString()} added · {result.updated.toLocaleString()} updated · safety backup <span className="font-mono">{result.safety_backup}</span>
        </div>
      )}
    </LaunchCard>
  );
}
